import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { CertificateStatus, Services, TrackingEvents } from '@estercare/ester-shared'
import { BodyLarge } from '../../../components/styles/Typography'
import { ScreeningViewTemplate } from '../../../components/screening/screeningView/ScreeningTemplate'
import { useConsent } from '../../../components/screening/ScreeningConsent'
import { ConsentId } from '../../../api/ester-b2b-api/private/consent/types'
import { useStartCertificationScreeningMutation } from '../../../api/ester-b2b-api/private/certification/certificationQueries'
import { useGetScreeningStatusQuery } from '../../../api/ester-b2b-api/private/screening/screeningQueries'
import { useCertificationStore, useScreeningQuestionnaireStore } from '../../../state/store'
import { useSelectActiveServices, useSelectCertificationScreeningId } from '../../../state/selectors'
import { useEmitEvent } from '../../../hooks/analytics/useEmitEvent'
import { queryErrorHandlerWithMessage } from '../../../utils/queryErrorHandler'
import { ROUTES } from '../../../constants/routes'
import { theme } from '../../../theme/theme'
import { LoadingSpinner } from '../../../components/LoadingSpinner'
import { CertificationResults } from './Results'

export const Certification = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const emitEvent = useEmitEvent()

  const { consentData, isConsentLoading, setUserConsent } = useConsent(ConsentId.SCREENING_CONSENT)
  const { mutateAsync: startCertificationScreening, isPending } = useStartCertificationScreeningMutation()
  const certificationScreeningId = useSelectCertificationScreeningId()
  const setCertificationScreeningId = useCertificationStore((state) => state.setScreeningId)
  const resetCertification = useCertificationStore((state) => state.resetState)
  const resetQuestionnaire = useScreeningQuestionnaireStore((state) => state.resetState)
  const { data: screeningStatusData, isLoading: isScreeningStatusLoading } = useGetScreeningStatusQuery()
  const certificationStatus = screeningStatusData?.certificateStatus
  const activeServices = useSelectActiveServices()

  const [error, setError] = useState<string | null>(null)

  const isInProgress = certificationStatus === CertificateStatus.CERTIFICATE_INCOMPLETE

  const startOrResumeScreening = async () => {
    try {
      const screening = await startCertificationScreening()
      resetQuestionnaire()
      // A fresh screening must not inherit documents attached to the one before it.
      if (screening.id !== certificationScreeningId) resetCertification()
      setCertificationScreeningId(screening.id)
      navigate(ROUTES.ADMIN_CERTIFICATION_QUESTIONS)
    } catch (startError) {
      setError(queryErrorHandlerWithMessage('start certification screening', t)(startError as Error))
    }
  }

  const onButtonClick = () => {
    emitEvent({
      eventType: TrackingEvents.CLICK,
      action: isInProgress ? 'certification_continue_screening' : 'certification_start_screening',
    })
    setError(null)

    if (consentData) {
      void startOrResumeScreening()
      return
    }

    setUserConsent(ConsentId.SCREENING_CONSENT, {
      onSuccess: () => {
        void startOrResumeScreening()
      },
      onError: (consentError) => {
        setError(queryErrorHandlerWithMessage('set user screening consent', t)(consentError))
      },
    })
  }

  if (!activeServices.includes(Services.CERTIFICATION)) return <Navigate to={ROUTES.ADMIN_SERVICES} replace />

  if (isScreeningStatusLoading) return <LoadingSpinner />

  if (certificationStatus === CertificateStatus.CERTIFICATE_HANDLED) return <CertificationResults />

  if (certificationStatus === CertificateStatus.CERTIFICATE_PENDING) {
    return (
      <ScreeningViewTemplate
        pageTitle={t('admin.certification.pageTitle')}
        screeningTitle={t('admin.certification.pendingTitle')}
        screeningDescription={t('admin.certification.pendingDescription')}
        hideButton
        closePath={ROUTES.ADMIN_SERVICES}
      />
    )
  }

  return (
    <ScreeningViewTemplate
      pageTitle={t('admin.certification.pageTitle')}
      screeningTitle={t('admin.certification.title')}
      screeningDescription={t('admin.certification.description')}
      screeningTitleChildren={<ConsentText>{t('admin.certification.consentText')}</ConsentText>}
      buttonText={isInProgress ? t('admin.certification.continueButton') : t('admin.certification.startButton')}
      onButtonClick={onButtonClick}
      isButtonDisabled={isConsentLoading}
      isLoading={isPending}
      errorMsg={error || undefined}
      closePath={ROUTES.ADMIN_SERVICES}
    />
  )
}

const ConsentText = styled(BodyLarge)`
  margin-top: ${theme.spacing.medium}px;
`
